import { forwardRef, useId } from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import { CoreUiComponents, type InputErrorType } from "@b2bportal/core-types";
import { useI18nContext, type DateTimeFormatStyle } from "@hopper-b2b/i18n";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import defaultStyles from "./DatePickerButton.module.scss";

export interface DatePickerButtonProps {
  dateFormat?: DateTimeFormatStyle;
  startDate?: string;
  endDate?: string;
  disabled?: boolean;
  startLabel: string;
  endLabel: string;
  onClick?: () => void;
  hideEndDate?: boolean;
  hideSeparator?: boolean;
  ariaLabel?: string;
  className?: string;
  error?: InputErrorType;
  collectiveDate?: boolean;
  placeholder?: string;
}

interface ContentProps {
  label: string;
  placeholder: string;
  date?: string;
}

export const DatePickerButton = forwardRef<
  HTMLButtonElement,
  DatePickerButtonProps
>((props, ref) => {
  const {
    startDate,
    endDate,
    startLabel,
    endLabel,
    disabled,
    onClick,
    dateFormat,
    hideEndDate,
    ariaLabel,
    className,
    error,
    collectiveDate = false,
    placeholder,
  } = props;

  const COMPONENT_KEY = CoreUiComponents.DatePickerButton;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, {
    error: error != null,
  });
  const { formatDateTime, DateTimeFormatStyle, t } = useI18nContext();
  const defaultDateTimeFormat = DateTimeFormatStyle.ShortMonthDayShortWeekday;
  const errorId = useId();

  const calendarIcon = (
    <Icon className={cn("icon")} iconName={IconNameEnum.calendar} />
  );

  const Content = ({ date, label, placeholder }: ContentProps) => {
    const content = date
      ? `${formatDateTime(
          dayjs(date).toDate(),
          dateFormat ?? defaultDateTimeFormat
        )}${
          collectiveDate && endDate
            ? ` - ${formatDateTime(
                dayjs(endDate).toDate(),
                dateFormat ?? defaultDateTimeFormat
              )}`
            : ""
        }`
      : placeholder;

    return (
      <div className={cn("button-content")}>
        <div className={cn("content-top-label")}>{label}</div>
        <div
          className={clsx(
            cn("content-main-content", {
              label: !date,
              error: error != null,
              nowrap: content.length <= 16,
            })
          )}
        >
          {content}
        </div>
      </div>
    );
  };

  const startDateComponent = (
    <div className={cn("start-date-container")}>
      {calendarIcon}
      <Content
        date={startDate}
        label={startLabel}
        placeholder={placeholder ?? t("core-ui.date.placeholders.startDate")}
      />
    </div>
  );

  const endDateComponent = (
    <div className={cn("end-date-container")}>
      {calendarIcon}
      <Content
        date={endDate}
        label={endLabel}
        placeholder={placeholder ?? t("core-ui.date.placeholders.endDate")}
      />
    </div>
  );

  const separatorIcon = (
    <div className={cn("separator-container")}>
      <Icon iconName={IconNameEnum.calendarSeparatorIcon} />
    </div>
  );

  return (
    <>
      <button
        className={clsx(block, className)}
        aria-label={ariaLabel}
        aria-describedby={error != null ? errorId : undefined}
        disabled={disabled}
        onClick={onClick}
        type="button"
        ref={ref}
      >
        {startDateComponent}
        {!hideEndDate && separatorIcon}
        {!hideEndDate && endDateComponent}
      </button>
      {error != null && (
        <VisuallyHidden asChild>
          <div id={errorId} role="alert">
            {t("requiredField")}
          </div>
        </VisuallyHidden>
      )}
    </>
  );
});
